import { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Grid, Alert, Typography, Snackbar } from '@mui/material';
import CategoryCard from './CategoryCard';
import CategoryEditModal from './CategoryEditModal';
import { apiRequestJson } from '../../api/apiService';
import { selectCategory, updateCategory } from '../../store/slices/productsSlice';
import { useLocalData } from '../../hooks/useLocalData';
import globalContextManager from '../../utils/globalContextManager';

/**
 * Verifica se usuário é admin.
 * @param {Object} user - Objeto do usuário
 * @returns {boolean} True se admin
 */
const isAdmin = user => {
  return user?.roles?.some(role => role.alias === 'admin') || false;
};

/**
 * Componente container para exibição de categorias.
 * Carrega e exibe categorias da API com layout responsivo otimizado.
 * Mobile: 1 card por linha, Tablet: 2 cards por linha, Desktop: 4 cards por linha.
 * Implementa scroll automático para centralizar a última categoria visualizada.
 * Admin: vê todas as categorias + botão editar.
 * Não-admin: vê apenas categorias ativas, sem botão editar.
 *
 * @component
 * @returns {JSX.Element} Container responsivo com grid de categorias e navegação contextual
 */
export default function Category() {
  const dispatch = useDispatch();
  const { lastViewedCategory } = useSelector(state => state.products);
  const { user } = useSelector(state => state.auth);
  const { categories } = useLocalData(); // Usar dados locais
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCategoryEdit, setSelectedCategoryEdit] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const categoryRefs = useRef({});

  const isUserAdmin = useMemo(() => isAdmin(user), [user]);

  // Dados são carregados via contexto global - não precisa de useEffect para fetch

  // Scroll para a última categoria visualizada
  useEffect(() => {
    if (lastViewedCategory && categories.length > 0) {
      const categoryElement = categoryRefs.current[lastViewedCategory.id];
      if (categoryElement) {
        setTimeout(() => {
          categoryElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }, 100);
      }
    }
  }, [lastViewedCategory, categories]);

  /**
   * Categorias filtradas baseado na role do usuário.
   * Admin: todas as categorias.
   * Não-admin: apenas categorias ativas.
   */
  const filteredCategories = useMemo(() => {
    if (isUserAdmin) {
      return categories; // Admin vê todas
    }
    return categories.filter(cat => cat.enable); // Outros vêem só ativas
  }, [categories, isUserAdmin]);

  const handleCategoryClick = category => {
    // Seleciona a categoria no estado - produtos serão filtrados localmente
    dispatch(selectCategory(category));
    console.log('Categoria selecionada (dados locais):', category.name);
  };

  const handleEditCategory = category => {
    setSelectedCategoryEdit(category);
    setEditModalOpen(true);
  };

  const handleSaveCategory = async data => {
    try {
      // Cenário B: Atualização remota (admin)
      await dispatch(updateCategory(data)).unwrap();

      // Atualiza contexto global após operação bem-sucedida
      await globalContextManager.forceUpdate('categories');

      setSnackbar({ open: true, message: 'Categoria atualizada com sucesso', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Erro ao atualizar categoria', severity: 'error' });
      throw error;
    }
  };

  // Loading e erro são gerenciados pelo AppInitializer via contexto global

  return (
    <Box component='main' sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 4,
          mt: { xs: 5, sm: 5, md: '100px' },
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        <Typography
          variant='h5' 
          component='h5'
          sx={{
            mt: { xs: 8, sm: 8, md: 8 },
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          Categorias de Produtos
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            container
            spacing={{ xs: 1, sm: 3, md: 3 }}
            sx={{
              maxWidth: { xs: '100%', sm: 1000, md: 1200 },
              width: '100%',
              margin: 0,
              mt: { xs: 1, md: 1 },
            }}
          >
            {filteredCategories.length > 0 ? (
              filteredCategories.map(category => (
                <Grid
                  key={category.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    maxWidth: '100%',
                  }}
                >
                  <div
                    ref={el => (categoryRefs.current[category.id] = el)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    <CategoryCard
                      id={category.id}
                      name={category.name}
                      parent_id={category.parent_id}
                      enable={category.enable}
                      image_url={category.image_url}
                      isAdmin={isUserAdmin}
                      onClick={handleCategoryClick}
                      onEdit={isUserAdmin ? handleEditCategory : undefined}
                    />
                  </div>
                </Grid>
              ))
            ) : (
              <Grid size={12}>
                <Alert severity='info'>Nenhuma categoria encontrada</Alert>
              </Grid>
            )}
          </Grid>
        </Box>

        <CategoryEditModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          category={selectedCategoryEdit}
          onSave={handleSaveCategory}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    </Box>
  );
}
